import axios, { AxiosResponse } from 'axios';
import { ThunkAction } from 'redux-thunk';
import ApiClient, { createTokenConfig, isCancelled } from 'api/ApiClient';
import { NotificationsAction, showError } from '@spike/notifications-action';
import store, { RootState } from 'store';
import {
    SessionAssignmentError,
    SESSION_AREA_LABELS,
    SessionBusinessArea,
    ServiceSessionDay,
    SessionSpecificAssignment,
    SESSION_ZONE_REQUIRED_MESSAGE
} from 'model/ServiceSession';
import { convertDefaultSaveToDto, convertSpecificSaveToDto, convertWeek } from './ServiceSessionsConverter';
import { SaveServiceSessionsResponseDto, ServiceSessionsWeekDto } from './ServiceSessionsDtos';
import {
    SERVICE_SESSIONS_CLEAR_WARNINGS_ACTION_TYPE,
    SERVICE_SESSIONS_ERROR_ACTION_TYPE,
    SERVICE_SESSIONS_FETCH_START_ACTION_TYPE,
    SERVICE_SESSIONS_FETCH_SUCCESS_ACTION_TYPE,
    SERVICE_SESSIONS_RESET_ACTION_TYPE,
    SERVICE_SESSIONS_SAVE_START_ACTION_TYPE,
    SERVICE_SESSIONS_SAVE_SUCCESS_ACTION_TYPE,
    ServiceSessionsAction
} from './ServiceSessionsActionsTypes';

type ServiceSessionsThunk = ThunkAction<void, RootState, null, ServiceSessionsAction | NotificationsAction>;

const serviceSessionsUrl = '/service_sessions';

const marketplaceId = (): number | undefined => store.getState().login.auth.marketplaceId;
const token = (): string => store.getState().login.auth.token!;

const withMarketplace = (path: string): string => `${path}?marketplace_id=${marketplaceId()}`;

const sessionsLabel = (businessArea?: SessionBusinessArea): string =>
    businessArea ? `${SESSION_AREA_LABELS[businessArea].toLowerCase()} sessions` : 'sessions';

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return !!value && typeof value === 'object' && !Array.isArray(value);
};

const toStaffId = (value: unknown): number | undefined => {
    const id = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
    return Number.isFinite(id) ? id : undefined;
};

const normalizeServiceSessionsMessage = (message: string): string => {
    return message.toLowerCase().includes("can't be blank for an in home service")
        ? SESSION_ZONE_REQUIRED_MESSAGE
        : message;
};

const messagesFromFieldErrors = (errors: unknown): Array<string> => {
    if (!isRecord(errors)) return [];

    return Object.values(errors)
        .flatMap(value => (Array.isArray(value) ? value : []))
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .map(normalizeServiceSessionsMessage);
};

const asAssignmentError = (value: unknown): SessionAssignmentError | undefined => {
    if (!isRecord(value) || value.errors === undefined) return undefined;

    const staffId = toStaffId(value.staff_id);
    const time = typeof value.start_time === 'string' ? value.start_time.slice(0, 5) : undefined;

    if (staffId === undefined || !time) return undefined;

    const messages = messagesFromFieldErrors(value.errors);

    return {
        staffId,
        time,
        messages: messages.length > 0 ? messages : [SESSION_ZONE_REQUIRED_MESSAGE]
    };
};

export const parseServiceSessionsAssignmentErrors = (apiError: unknown): Array<SessionAssignmentError> => {
    const data = axios.isAxiosError(apiError) ? apiError.response?.data : undefined;
    const found: Array<SessionAssignmentError> = [];
    const seen = new Set<string>();

    const visit = (value: unknown) => {
        const parsed = asAssignmentError(value);

        if (parsed) {
            const key = `${parsed.staffId}|${parsed.time}`;
            if (!seen.has(key)) {
                seen.add(key);
                found.push(parsed);
            }
            return;
        }

        if (Array.isArray(value)) {
            value.forEach(visit);
            return;
        }

        if (isRecord(value)) {
            Object.values(value).forEach(visit);
        }
    };

    visit(data);
    return found;
};

export const parseServiceSessionsError = (apiError: unknown, businessArea?: SessionBusinessArea): string => {
    const assignmentError = parseServiceSessionsAssignmentErrors(apiError)[0];
    if (assignmentError?.messages[0]) return assignmentError.messages[0];

    const data = axios.isAxiosError(apiError) ? apiError.response?.data : undefined;

    if (data && typeof data.error === 'string') return normalizeServiceSessionsMessage(data.error);

    if (data && typeof data === 'object') {
        const messages = Object.values(data as Record<string, unknown>)
            .flatMap(value => (Array.isArray(value) ? value : []))
            .filter((value): value is string => typeof value === 'string');

        if (messages[0]) return normalizeServiceSessionsMessage(messages[0]);
    }

    return `Error saving ${sessionsLabel(businessArea)}.`;
};

const addIsoDays = (isoDate: string, amount: number): string => {
    const date = new Date(`${isoDate}T00:00:00`);
    date.setDate(date.getDate() + amount);

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const fetchServiceSessionsThunk = (
    businessArea: SessionBusinessArea,
    startDate: string
): ServiceSessionsThunk => {
    return async dispatch => {
        dispatch(fetchStart(businessArea, startDate));

        // Two extra weeks so a weekly default can be read from an occurrence no date override emptied.
        const startDates = [startDate, addIsoDays(startDate, 7), addIsoDays(startDate, 14)];

        try {
            const responses = await Promise.all(
                startDates.map(date =>
                    ApiClient.get<ServiceSessionsWeekDto>(
                        `${withMarketplace(serviceSessionsUrl)}&start_date=${date}&business_area=${businessArea}`,
                        createTokenConfig(token())
                    )
                )
            );
            const current = store.getState().serviceSessions;
            if (current.businessArea !== businessArea || current.startDate !== startDate) return;

            const daysByDate = new Map<string, ServiceSessionDay>();

            responses.forEach(response => {
                convertWeek(response.data).days.forEach(day => {
                    daysByDate.set(day.date, day);
                });
            });

            const displayedWeek = convertWeek(responses[0].data);
            dispatch(fetchSuccess(displayedWeek.startDate, displayedWeek.endDate, Array.from(daysByDate.values())));
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                dispatch(error(parseServiceSessionsError(apiError, businessArea)));
                dispatch(showError(`Error fetching ${sessionsLabel(businessArea)}.`));
            }
        }
    };
};

const refetchCurrentWeek = (): ServiceSessionsThunk => {
    return async dispatch => {
        const { businessArea, startDate } = store.getState().serviceSessions;
        if (businessArea && startDate) {
            await dispatch(fetchServiceSessionsThunk(businessArea, startDate));
        }
    };
};

type SaveSessionsCallback = (warnings: Array<string>) => void;

export interface DefaultSessionsChange {
    weekday: number;
    assignments: Array<SessionSpecificAssignment>;
}

const assignmentsSignature = (assignments: Array<SessionSpecificAssignment>): string =>
    JSON.stringify(assignments.map(assignment => [assignment.staffId, assignment.time, assignment.zone ?? null]));

const groupChangesByAssignments = (
    changes: Array<DefaultSessionsChange>
): Array<{ weekdays: Array<number>; assignments: Array<SessionSpecificAssignment> }> => {
    const batches: Array<{
        signature: string;
        weekdays: Array<number>;
        assignments: Array<SessionSpecificAssignment>;
    }> = [];

    changes.forEach(change => {
        const signature = assignmentsSignature(change.assignments);
        const batch = batches.find(item => item.signature === signature);

        if (batch) {
            batch.weekdays.push(change.weekday);
        } else {
            batches.push({ signature, weekdays: [change.weekday], assignments: change.assignments });
        }
    });

    return batches;
};

export const saveDefaultSessionsBatchThunk = (
    serviceId: number,
    changes: Array<DefaultSessionsChange>,
    callback?: SaveSessionsCallback,
    errorCallback?: (weekday: number) => void
): ServiceSessionsThunk => {
    return async dispatch => {
        dispatch(saveStart());

        const batches = groupChangesByAssignments(changes);
        let currentWeekday = batches[0]?.weekdays[0];
        let savedWeekdays = 0;

        try {
            const warnings: Array<string> = [];

            for (const batch of batches) {
                currentWeekday = batch.weekdays[0];
                const response: AxiosResponse<SaveServiceSessionsResponseDto> = await ApiClient.put(
                    withMarketplace(`${serviceSessionsUrl}/defaults`),
                    convertDefaultSaveToDto(serviceId, batch.weekdays, batch.assignments),
                    createTokenConfig(token())
                );
                warnings.push(...(response.data.warnings ?? []));
                savedWeekdays += batch.weekdays.length;
            }

            const uniqueWarnings = Array.from(new Set(warnings));
            dispatch(saveSuccess(uniqueWarnings));
            await dispatch(refetchCurrentWeek());
            callback?.(uniqueWarnings);
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                const assignmentErrors = parseServiceSessionsAssignmentErrors(apiError);
                const message = parseServiceSessionsError(apiError, store.getState().serviceSessions.businessArea);
                dispatch(error(message, assignmentErrors));
                dispatch(
                    showError(savedWeekdays > 0 ? `${message} The days saved before the error were kept.` : message)
                );
                await dispatch(refetchCurrentWeek());
                if (currentWeekday !== undefined) errorCallback?.(currentWeekday);
            }
        }
    };
};

export const saveSpecificSessionsThunk = (
    serviceId: number,
    sessionDate: string,
    assignments: Array<SessionSpecificAssignment>,
    callback?: SaveSessionsCallback
): ServiceSessionsThunk => {
    return async dispatch => {
        dispatch(saveStart());

        try {
            const response: AxiosResponse<SaveServiceSessionsResponseDto> = await ApiClient.put(
                withMarketplace(`${serviceSessionsUrl}/specific`),
                convertSpecificSaveToDto(serviceId, sessionDate, assignments),
                createTokenConfig(token())
            );
            const warnings = response.data.warnings ?? [];
            dispatch(saveSuccess(warnings));
            await dispatch(refetchCurrentWeek());
            callback?.(warnings);
        } catch (apiError) {
            if (!isCancelled(apiError)) {
                const assignmentErrors = parseServiceSessionsAssignmentErrors(apiError);
                const message = parseServiceSessionsError(apiError, store.getState().serviceSessions.businessArea);
                dispatch(error(message, assignmentErrors));
                dispatch(showError(message));
            }
        }
    };
};

export const reset = (): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_RESET_ACTION_TYPE
});

export const clearServiceSessionsWarnings = (): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_CLEAR_WARNINGS_ACTION_TYPE
});

const fetchStart = (businessArea: SessionBusinessArea, startDate: string): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_FETCH_START_ACTION_TYPE,
    payload: { businessArea, startDate }
});

const fetchSuccess = (startDate: string, endDate: string, days: Array<ServiceSessionDay>): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_FETCH_SUCCESS_ACTION_TYPE,
    payload: { startDate, endDate, days }
});

const saveStart = (): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_SAVE_START_ACTION_TYPE
});

const saveSuccess = (warnings: Array<string>): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_SAVE_SUCCESS_ACTION_TYPE,
    payload: { warnings }
});

const error = (message?: string, assignmentErrors: Array<SessionAssignmentError> = []): ServiceSessionsAction => ({
    type: SERVICE_SESSIONS_ERROR_ACTION_TYPE,
    payload: { error: message, assignmentErrors }
});
